<template>
  <div
    :class="[
      theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
      'border rounded-lg overflow-hidden'
    ]"
  >
    <div class="overflow-x-auto">
      <table class="w-full border-separate border-spacing-0 text-sm">
        <thead class="sticky top-0 z-10 bg-blue-500 dark:bg-blue-600">
          <tr>
            <th
              v-for="(column, index) in columns"
              :key="column.key"
              :class="[
                'px-4 py-3 text-left font-semibold text-white uppercase text-xs tracking-wide cursor-pointer select-none hover:bg-blue-600 dark:hover:bg-blue-700 transition-all relative',
                column.sortable ? 'cursor-pointer' : ''
              ]"
              :style="
                columnWidths[index]
                  ? { width: `${columnWidths[index]}px`, minWidth: `${columnWidths[index]}px` }
                  : {}
              "
              @click="column.sortable && $emit('sort', index)"
            >
              <span class="flex items-center gap-1">
                {{ column.label }}
                <span
                  v-if="column.sortable"
                  class="opacity-60"
                >
                  {{
                    sortConfig.column === index
                      ? sortConfig.direction === 'asc'
                        ? '↑'
                        : '↓'
                      : '↕'
                  }}
                </span>
              </span>
              <!-- Resizer -->
              <div
                v-if="column.resizable !== false"
                class="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-white/30 active:bg-white/50"
                @mousedown.stop="startResize($event, index)"
              ></div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="team in sortedTeams"
            :key="team.id"
            :class="[
              'border-l-2 border-l-transparent transition-all cursor-pointer',
              theme.isDark.value
                ? 'hover:bg-gray-700 hover:border-l-blue-500'
                : 'hover:bg-blue-50 hover:border-l-blue-500'
            ]"
            @click="$emit('view', team)"
          >
            <!-- Team Name -->
            <td
              class="px-4 py-3 border-b border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white font-medium"
            >
              {{ team.name || '-' }}
            </td>
            <!-- Director -->
            <td
              class="px-4 py-3 border-b border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300"
            >
              {{ getManagerEmails(team.managers, 'DIRECTOR') }}
            </td>
            <!-- Folder ID -->
            <td
              class="px-4 py-3 border-b border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-mono text-xs"
            >
              {{ team.standupId || '-' }}
            </td>
            <!-- Roadmap -->
            <td
              class="px-4 py-3 border-b border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-mono text-xs"
            >
              {{ team.roadmapBoardId || '-' }}
            </td>
            <!-- EM Email -->
            <td
              class="px-4 py-3 border-b border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300"
            >
              {{ getManagerEmails(team.managers, 'EM') }}
            </td>
            <!-- TL Email -->
            <td
              class="px-4 py-3 border-b border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300"
            >
              {{ getManagerEmails(team.managers, 'TL') }}
            </td>
            <!-- Developer Emails -->
            <td class="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div class="flex flex-wrap gap-1">
                <span
                  v-for="dev in (team.devs || []).slice(0, 4)"
                  :key="dev.emailPrefix"
                  class="inline-block px-2 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600"
                >
                  {{ dev.emailPrefix }}
                </span>
                <span
                  v-if="(team.devs || []).length > 4"
                  class="inline-block px-2 py-0.5 rounded text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300"
                >
                  +{{ team.devs.length - 4 }} more
                </span>
                <span
                  v-if="!team.devs || team.devs.length === 0"
                  class="text-gray-400"
                  >-</span
                >
              </div>
            </td>
            <!-- PM List -->
            <td
              class="px-4 py-3 border-b border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300"
            >
              {{ getPMList(team.managers) }}
            </td>
            <!-- Is Deleted -->
            <td class="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <span
                :class="[
                  'inline-block px-2.5 py-1 rounded text-xs font-semibold uppercase tracking-wide border',
                  team.deleted
                    ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 border-red-300 dark:border-red-700'
                    : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 border-green-300 dark:border-green-700'
                ]"
              >
                {{ team.deleted ? 'Yes' : 'No' }}
              </span>
            </td>
            <!-- Actions -->
            <td
              class="px-4 py-3 border-b border-gray-200 dark:border-gray-700"
              @click.stop
            >
              <div class="relative">
                <button
                  class="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-gray-600 dark:text-gray-300"
                  @click="toggleMenu(team.id)"
                >
                  ⋯
                </button>
                <!-- Dropdown Menu -->
                <div
                  v-if="openMenuId === team.id"
                  class="absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg min-w-[150px] z-20 overflow-hidden"
                >
                  <button
                    class="w-full px-4 py-2 text-left text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-gray-700 flex items-center gap-2"
                    @click="handleEdit(team)"
                  >
                    <span>✏️</span>
                    <span>Edit</span>
                  </button>
                  <button
                    class="w-full px-4 py-2 text-left text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                    @click="handleDuplicate(team)"
                  >
                    <span>📋</span>
                    <span>Duplicate</span>
                  </button>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, onMounted, onUnmounted } from 'vue'
import { useTheme } from '../../../utils/useTheme'
import { taskSentinelService } from '../../../services/task-sentinel'
import type { Team, SortConfig, Manager } from '../../../types/task-sentinel'

const theme = inject('theme', useTheme())

const props = defineProps<{
  teams: Team[]
  sortConfig: SortConfig
}>()

const emit = defineEmits<{
  (e: 'sort', column: number): void
  (e: 'view', team: Team): void
  (e: 'duplicate', team: Team): void
}>()

// Column definitions
const columns = [
  { key: 'name', label: 'Team', sortable: true, resizable: true },
  { key: 'director', label: 'Director', sortable: true, resizable: true },
  { key: 'standupId', label: 'Folder ID', sortable: true, resizable: true },
  { key: 'roadmap', label: 'Roadmap', sortable: true, resizable: true },
  { key: 'em', label: 'EM Email', sortable: true, resizable: true },
  { key: 'tl', label: 'TL Email', sortable: true, resizable: true },
  { key: 'devs', label: 'Developer Emails', sortable: true, resizable: true },
  { key: 'pm', label: 'PM List', sortable: true, resizable: true },
  { key: 'deleted', label: 'Is Paused?', sortable: true, resizable: true },
  { key: 'actions', label: 'Actions', sortable: false, resizable: false }
]

// State
const openMenuId = ref<string | null>(null)
const columnWidths = ref<Record<number, number>>({})
const isResizing = ref(false)
const resizingColumn = ref<number | null>(null)
const startX = ref(0)
const startWidth = ref(0)

// Load saved column widths
onMounted(() => {
  const saved = localStorage.getItem('taskSentinel_columnWidths')
  if (saved) {
    try {
      columnWidths.value = JSON.parse(saved)
    } catch (e) {
      // Ignore parse errors
    }
  }

  // Close menu on outside click
  document.addEventListener('click', handleOutsideClick)
})

onUnmounted(() => {
  document.removeEventListener('click', handleOutsideClick)
  document.removeEventListener('mousemove', handleResizeMove)
  document.removeEventListener('mouseup', handleResizeEnd)
})

// Computed
const sortedTeams = computed(() => {
  if (props.sortConfig.column < 0) return props.teams

  const sorted = [...props.teams].sort((a, b) => {
    let aVal: string
    let bVal: string

    switch (props.sortConfig.column) {
      case 0:
        aVal = (a.name || '').toLowerCase()
        bVal = (b.name || '').toLowerCase()
        break
      case 1:
        aVal = getManagerEmails(a.managers, 'DIRECTOR').toLowerCase()
        bVal = getManagerEmails(b.managers, 'DIRECTOR').toLowerCase()
        break
      case 2:
        aVal = (a.standupId || '').toLowerCase()
        bVal = (b.standupId || '').toLowerCase()
        break
      case 3:
        aVal = (a.roadmapBoardId || '').toLowerCase()
        bVal = (b.roadmapBoardId || '').toLowerCase()
        break
      case 4:
        aVal = getManagerEmails(a.managers, 'EM').toLowerCase()
        bVal = getManagerEmails(b.managers, 'EM').toLowerCase()
        break
      case 5:
        aVal = getManagerEmails(a.managers, 'TL').toLowerCase()
        bVal = getManagerEmails(b.managers, 'TL').toLowerCase()
        break
      case 6:
        aVal = taskSentinelService.getDeveloperEmails(a.devs).toLowerCase()
        bVal = taskSentinelService.getDeveloperEmails(b.devs).toLowerCase()
        break
      case 7:
        aVal = getPMList(a.managers).toLowerCase()
        bVal = getPMList(b.managers).toLowerCase()
        break
      case 8:
        aVal = a.deleted ? 'yes' : 'no'
        bVal = b.deleted ? 'yes' : 'no'
        break
      default:
        return 0
    }

    if (aVal < bVal) return props.sortConfig.direction === 'asc' ? -1 : 1
    if (aVal > bVal) return props.sortConfig.direction === 'asc' ? 1 : -1
    return 0
  })

  return sorted
})

// Methods
const getManagerEmails = (managers: Manager[] | undefined, type: string): string => {
  return taskSentinelService.getManagerEmails(managers, type)
}

const getPMList = (managers: Manager[] | undefined): string => {
  return taskSentinelService.getPMList(managers)
}

const toggleMenu = (teamId: string) => {
  openMenuId.value = openMenuId.value === teamId ? null : teamId
}

const handleOutsideClick = (e: MouseEvent) => {
  const target = e.target as HTMLElement
  if (!target.closest('.relative')) {
    openMenuId.value = null
  }
}

const handleEdit = (team: Team) => {
  openMenuId.value = null
  emit('view', team)
}

const handleDuplicate = (team: Team) => {
  openMenuId.value = null
  emit('duplicate', team)
}

// Column resizing
const startResize = (e: MouseEvent, columnIndex: number) => {
  isResizing.value = true
  resizingColumn.value = columnIndex
  startX.value = e.pageX
  startWidth.value = columnWidths.value[columnIndex] || 150

  document.addEventListener('mousemove', handleResizeMove)
  document.addEventListener('mouseup', handleResizeEnd)
  document.body.style.userSelect = 'none'
  document.body.style.cursor = 'col-resize'
}

const handleResizeMove = (e: MouseEvent) => {
  if (!isResizing.value || resizingColumn.value === null) return

  const diff = e.pageX - startX.value
  const newWidth = Math.max(50, startWidth.value + diff)
  columnWidths.value[resizingColumn.value] = newWidth
}

const handleResizeEnd = () => {
  isResizing.value = false
  resizingColumn.value = null

  document.removeEventListener('mousemove', handleResizeMove)
  document.removeEventListener('mouseup', handleResizeEnd)
  document.body.style.userSelect = ''
  document.body.style.cursor = ''

  // Save column widths
  localStorage.setItem('taskSentinel_columnWidths', JSON.stringify(columnWidths.value))
}
</script>
